import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Modal } from "./ui/modal";
import SelectInput from "./select-input";
import SearchInput from "./search-input";
import DataTable, { TableRowData } from "./data-table";
import SettingsSectionLoading from "./settings-section-loading";
import { RefreshToolbarButton } from "./refresh-toolbar-button";
import { attachmentTypesTableColumns } from "../lib/table-columns";
import {
  AttachmentContext,
  AttachmentType,
  AttachmentTypeStatus,
  createAttachmentType,
  deleteAttachmentType,
  getAttachmentTypes,
  updateAttachmentType,
} from "../lib/api";
import { getBackendErrorMessage } from "../lib/utils";

const CONTEXT_OPTIONS = [
  { value: "all", label: "All contexts" },
  { value: "PRODUCT_ORDER", label: "Product orders" },
  { value: "SITE_SERVICE_REQUEST", label: "Service requests" },
];

const CONTEXT_FORM_OPTIONS = [
  { value: "PRODUCT_ORDER", label: "Product orders" },
  { value: "SITE_SERVICE_REQUEST", label: "Service requests" },
];

const formatContextLabel = (context: AttachmentContext) =>
  context === "PRODUCT_ORDER" ? "Product orders" : "Service requests";

const AttachmentTypesSection: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState("");
  const [contextFilter, setContextFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [context, setContext] = useState<AttachmentContext>("PRODUCT_ORDER");
  const [allowedExtensions, setAllowedExtensions] = useState("");
  const [maxFileSizeMb, setMaxFileSizeMb] = useState("10");
  const [maxFilesPerEntity, setMaxFilesPerEntity] = useState("1");
  const [description, setDescription] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [status, setStatus] = useState<AttachmentTypeStatus>("ACTIVE");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const contextParam =
    contextFilter === "all" ? undefined : (contextFilter as AttachmentContext);

  const attachmentTypesQuery = useQuery({
    queryKey: ["attachment-types", { context: contextParam }],
    queryFn: async () => getAttachmentTypes(contextParam),
  });

  useEffect(() => {
    if (!attachmentTypesQuery.error) return;
    toast.error(
      getBackendErrorMessage(attachmentTypesQuery.error, "Failed to load attachment types"),
    );
  }, [attachmentTypesQuery.error]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchInput, contextFilter]);

  const filteredRows = useMemo(() => {
    const search = searchInput.trim().toLowerCase();
    const data = attachmentTypesQuery.data ?? [];
    return data
      .filter((t) => {
        if (!search) return true;
        return (
          t.name.toLowerCase().includes(search) ||
          t.context.toLowerCase().includes(search) ||
          (t.description ?? "").toLowerCase().includes(search) ||
          t.allowedExtensions.toLowerCase().includes(search)
        );
      })
      .sort((a, b) => {
        const aSort = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
        const bSort = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
        if (aSort !== bSort) return aSort - bSort;
        return a.name.localeCompare(b.name);
      });
  }, [attachmentTypesQuery.data, searchInput]);

  const tableRows: TableRowData[] = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize).map((t: AttachmentType) => ({
      id: t.id,
      name: t.name,
      context: formatContextLabel(t.context),
      allowed_extensions: t.allowedExtensions,
      max_file_size: String(t.maxFileSizeMb),
      max_files: String(t.maxFilesPerEntity),
      sort_order: t.sortOrder != null ? String(t.sortOrder) : "—",
      enabled: t.status === "ACTIVE",
      raw_data: t,
    }));
  }, [filteredRows, currentPage, pageSize]);

  const totalItems = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const tableLoading =
    attachmentTypesQuery.isFetching && !attachmentTypesQuery.isLoading;

  const resetForm = () => {
    setEditingId(null);
    setName("");
    setContext("PRODUCT_ORDER");
    setAllowedExtensions("");
    setMaxFileSizeMb("10");
    setMaxFilesPerEntity("1");
    setDescription("");
    setSortOrder("");
    setStatus("ACTIVE");
  };

  const openCreate = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const openEdit = (raw: AttachmentType) => {
    setEditingId(raw.id);
    setName(raw.name);
    setContext(raw.context);
    setAllowedExtensions(raw.allowedExtensions);
    setMaxFileSizeMb(String(raw.maxFileSizeMb));
    setMaxFilesPerEntity(String(raw.maxFilesPerEntity));
    setDescription(raw.description ?? "");
    setSortOrder(raw.sortOrder != null ? String(raw.sortOrder) : "");
    setStatus(raw.status);
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!name.trim()) return toast.error("Name is required");
    if (!allowedExtensions.trim()) return toast.error("Allowed extensions are required");

    const maxSize = Number(maxFileSizeMb);
    const maxFiles = Number(maxFilesPerEntity);
    const sort = sortOrder.trim() ? Number(sortOrder) : undefined;

    if (!Number.isFinite(maxSize) || maxSize <= 0) {
      return toast.error("Max file size must be a positive number");
    }
    if (!Number.isFinite(maxFiles) || maxFiles <= 0 || !Number.isInteger(maxFiles)) {
      return toast.error("Max files per entity must be a positive whole number");
    }
    if (sortOrder.trim() && !Number.isFinite(sort)) {
      return toast.error("Sort order must be a valid number");
    }

    setSaving(true);
    try {
      const descriptionValue = description.trim() || undefined;
      if (editingId) {
        await updateAttachmentType(editingId, {
          name: name.trim(),
          allowedExtensions: allowedExtensions.trim(),
          maxFileSizeMb: maxSize,
          maxFilesPerEntity: maxFiles,
          description: descriptionValue ?? null,
          sortOrder: sort ?? null,
          status,
        });
        toast.success("Attachment type updated");
      } else {
        await createAttachmentType({
          name: name.trim(),
          context,
          allowedExtensions: allowedExtensions.trim(),
          maxFileSizeMb: maxSize,
          maxFilesPerEntity: maxFiles,
          description: descriptionValue,
          sortOrder: sort,
          status,
        });
        toast.success("Attachment type created");
      }
      setIsModalOpen(false);
      resetForm();
      await queryClient.invalidateQueries({ queryKey: ["attachment-types"] });
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to save attachment type"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    setDeleting(true);
    try {
      await deleteAttachmentType(deletingId);
      toast.success("Attachment type deleted");
      setDeletingId(null);
      await queryClient.invalidateQueries({ queryKey: ["attachment-types"] });
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to delete attachment type"));
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleStatus = async (row: TableRowData, enabled: boolean) => {
    const id = row.id as string;
    setTogglingIds((prev) => new Set(prev).add(id));
    try {
      await updateAttachmentType(id, { status: enabled ? "ACTIVE" : "DISABLED" });
      toast.success(enabled ? "Attachment type enabled" : "Attachment type disabled");
      await queryClient.invalidateQueries({ queryKey: ["attachment-types"] });
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to update attachment type status"));
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const actions = [
    {
      label: "Edit attachment type",
      icon: Edit,
      onClick: (row: TableRowData) => openEdit((row as { raw_data: AttachmentType }).raw_data),
    },
    {
      label: "Delete attachment type",
      icon: Trash2,
      onClick: (row: TableRowData) => setDeletingId(row.id as string),
    },
  ];

  if (attachmentTypesQuery.isLoading) {
    return <SettingsSectionLoading />;
  }

  return (
    <>
      <div className="w-full space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <p className="text-sm text-gray-500">
            Configure attachment types for product orders and service requests. Mobile users
            upload files using these type names.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <RefreshToolbarButton
              isRefreshing={attachmentTypesQuery.isFetching}
              onRefresh={() =>
                queryClient.invalidateQueries({ queryKey: ["attachment-types"] })
              }
            />
            <Button onClick={openCreate}>Add attachment type</Button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <SearchInput
            searchTerm={searchInput}
            onSearchChange={setSearchInput}
            placeholder="Search attachment types..."
            className="w-full md:max-w-sm"
          />
          <SelectInput
            options={CONTEXT_OPTIONS}
            value={contextFilter}
            onChange={setContextFilter}
            className="w-full md:w-56"
          />
        </div>

        <DataTable
          columns={attachmentTypesTableColumns}
          data={tableRows}
          actions={actions}
          loading={tableLoading}
          enabledToggle={{
            onToggle: handleToggleStatus,
            isToggling: (rowId) => togglingIds.has(rowId),
          }}
          pagination={{
            page: currentPage,
            pageSize,
            totalItems,
            totalPages,
            onPageChange: setCurrentPage,
            onPageSizeChange: setPageSize,
          }}
        />
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        title={editingId ? "Edit attachment type" : "Add attachment type"}
      >
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="space-y-1.5">
            <Label htmlFor="attachment-name">Name</Label>
            <Input
              id="attachment-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='e.g. "Site Photo"'
            />
          </div>

          <div className="space-y-1.5">
            <Label>Context</Label>
            <SelectInput
              options={CONTEXT_FORM_OPTIONS}
              value={context}
              onChange={(v) => setContext(v as AttachmentContext)}
              disabled={!!editingId}
              className="w-full"
            />
            {editingId && (
              <p className="text-xs text-gray-500">Context cannot be changed after creation.</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="attachment-extensions">Allowed extensions</Label>
            <Input
              id="attachment-extensions"
              value={allowedExtensions}
              onChange={(e) => setAllowedExtensions(e.target.value)}
              placeholder="jpg,jpeg,png,pdf"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="attachment-max-size">Max file size (MB)</Label>
              <Input
                id="attachment-max-size"
                type="number"
                min={1}
                value={maxFileSizeMb}
                onChange={(e) => setMaxFileSizeMb(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="attachment-max-files">Max files per entity</Label>
              <Input
                id="attachment-max-files"
                type="number"
                min={1}
                step={1}
                value={maxFilesPerEntity}
                onChange={(e) => setMaxFilesPerEntity(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="attachment-sort">Sort order (optional)</Label>
            <Input
              id="attachment-sort"
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              placeholder="Lower numbers appear first"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="attachment-description">Description (optional)</Label>
            <textarea
              id="attachment-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Shown to staff or used internally"
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>

          {!editingId && (
            <div className="space-y-1.5">
              <Label>Status</Label>
              <SelectInput
                options={[
                  { value: "ACTIVE", label: "Active" },
                  { value: "DISABLED", label: "Disabled" },
                ]}
                value={status}
                onChange={(v) => setStatus(v as AttachmentTypeStatus)}
                className="w-full"
              />
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsModalOpen(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button disabled={saving} onClick={() => void handleSave()}>
              {saving ? "Saving..." : editingId ? "Update" : "Create"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={deletingId !== null}
        onClose={() => {
          if (!deleting) setDeletingId(null);
        }}
        title="Delete attachment type?"
        description="This action cannot be undone."
        className="max-w-md"
      >
        <div className="px-6 pt-5 pb-8 flex justify-end gap-3 flex-wrap shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => setDeletingId(null)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => void handleDelete()}
            disabled={deleting || !deletingId}
          >
            {deleting ? "Deleting…" : "Yes, delete attachment type"}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default AttachmentTypesSection;
